// Live-opdateringer: når nogen ændrer noget (kontoret, en steamer eller en kunde), ser alle andre det med det samme
// – uden at genindlæse siden. Skærmene henter data igen, når der kommer besked om en ændring (useData lytter på api.lyt).
//
//  • Testversionen: BroadcastChannel + storage-event mellem faner i samme browser.
//  • Drift (Supabase): Realtime "postgres_changes" på tabellerne nedenfor – ÉN kanal pr. indlogget bruger og virksomhed
//    (startSupabaseLive, startes af SupabaseApi efter login og stoppes ved log ud/skift af virksomhed). Kun virksomhedens rækker
//    (filter klient_id=eq.<id>), og Supabase sender kun rækker, brugeren må se (RLS gælder også for Realtime), så en kunde får
//    aldrig besked om andres bookinger. Se supabase/migrations/*_realtime.sql. Sider, der selv vil lytte (I dag), bruger hubben
//    (lytTabeller) – ingen ekstra kanaler.
//
// Hubben samler mange ændringer på kort tid (fx en hel ruteoptimering) til én opdatering, og henter alt igen,
// når forbindelsen kommer tilbage (mobil i lommen, wifi skifter), så ingen ser forældede data.

export type LiveStatus = 'live' | 'forbinder' | 'offline';
/** Tabellerne i publikationen supabase_realtime med kolonnen klient_id (tests/db/live.test.ts tjekker begge dele). */
export const LIVE_TABELLER = [
  'bookinger', 'ordrer', 'betalinger', 'kunder', 'biler', 'medlemskaber', 'steamere', 'fravaer', 'fravaer_anmodninger', 'fastfri_anmodninger', 'arbejdstider',
  'skadesager', 'beskeder', 'chat_samtaler', 'chat_beskeder', 'chat_laest', 'udlaeg', 'indbetalinger', 'dokumentation', 'profiler', 'indstillinger',
  'bot_samtaler', 'bot_beskeder',
] as const;
/** Ændringer fra andre samles så længe, før skærmene henter igen (en ruteoptimering eller import giver mange på én gang). */
export const FJERN_SAML_MS = 800;

type Lytter = (tabeller: Set<string>) => void;

export class LiveHub {
  private lyttere = new Set<Lytter>();
  private statusLyttere = new Set<() => void>();
  private ventende = new Set<string>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  status: LiveStatus = 'forbinder';
  sidst: number | null = null;

  /** Kaldes af kilden (demo eller Supabase), når en tabel er ændret. `saml` = så mange ms samles ændringerne, før skærmene får besked. */
  aendret(tabel: string, saml = 60) {
    this.ventende.add(tabel); this.sidst = Date.now();
    if (this.timer) return;
    this.timer = setTimeout(() => { const t = this.ventende; this.ventende = new Set(); this.timer = null; this.lyttere.forEach((f) => f(t)); }, saml);
  }
  /** Alt kan være ændret (fx efter forbindelsen har været væk). */
  alt() { this.aendret('*'); }
  saetStatus(s: LiveStatus) {
    if (s === this.status) return;
    const varVaek = this.status !== 'live';
    this.status = s; this.statusLyttere.forEach((f) => f());
    if (s === 'live' && varVaek) this.alt();
  }
  lyt(f: Lytter) { this.lyttere.add(f); return () => { this.lyttere.delete(f); }; }
  lytStatus(f: () => void) { this.statusLyttere.add(f); return () => { this.statusLyttere.delete(f); }; }
}
export const live = new LiveHub();

/** Testversionen: alle faner i samme browser deler data og får besked med det samme. */
export function startDemoLive(navn: string, hentIgen: () => void): { send: (tabel: string) => void } {
  let kanal: BroadcastChannel | null = null;
  try { kanal = new BroadcastChannel(navn); } catch { /* ældre browser: storage-event bruges */ }
  if (kanal) kanal.onmessage = (e) => { hentIgen(); live.aendret(String(e.data?.tabel ?? '*')); };
  try {
    addEventListener('storage', (e) => { if (e.key?.startsWith(navn) || e.key === 'pixie-platform-v1') { hentIgen(); live.aendret('*'); } });
    addEventListener('online', () => live.saetStatus('live'));
    addEventListener('offline', () => live.saetStatus('offline'));
  } catch { /* ingen vinduer i test */ }
  live.saetStatus(typeof navigator !== 'undefined' && navigator.onLine === false ? 'offline' : 'live');
  return { send: (tabel) => { try { kanal?.postMessage({ tabel, tid: Date.now() }); } catch { /* ok */ } } };
}

// ---------- Supabase Realtime (aktiveres, når Supabase er sat op) ----------
/** Det udsnit af supabase-js, vi bruger – så koden er klar uden at hente biblioteket i testversionen. */
export type SupabaseKlient = {
  channel(navn: string): SupabaseKanal;
  removeChannel(k: SupabaseKanal): unknown;
  realtime?: { setAuth?(token: string): void };
};
type SupabaseKanal = {
  on(type: 'postgres_changes', filter: { event: '*'; schema: string; table: string; filter?: string }, cb: (p: { table: string }) => void): SupabaseKanal;
  subscribe(cb?: (status: string) => void): SupabaseKanal;
};

/**
 * Lyt på virksomhedens ændringer i databasen (de rækker, brugeren har adgang til) – én kanal. Kaldes af SupabaseApi efter login;
 * stop (det returnerede) ved log ud eller skift af virksomhed. Genopretter selv forbindelsen: Supabase prøver igen automatisk, og
 * når fanen bliver synlig/online igen, hentes alt.
 */
export function startSupabaseLive(klient: SupabaseKlient, klientId: string): () => void {
  live.saetStatus('forbinder');
  const af = abonnerTabeller(klient, 'pixie-live-' + klientId, LIVE_TABELLER, `klient_id=eq.${klientId}`, (t) => live.aendret(t, FJERN_SAML_MS),
    (s) => live.saetStatus(s === 'SUBSCRIBED' ? 'live' : s === 'CLOSED' || s === 'CHANNEL_ERROR' || s === 'TIMED_OUT' ? 'offline' : 'forbinder'));
  const synlig = () => { if (document.visibilityState === 'visible') live.alt(); };
  const online = () => live.alt();
  const offline = () => live.saetStatus('offline');
  document.addEventListener('visibilitychange', synlig); addEventListener('online', online); addEventListener('offline', offline);
  return () => {
    af(); live.saetStatus('forbinder');
    document.removeEventListener('visibilitychange', synlig); removeEventListener('online', online); removeEventListener('offline', offline);
  };
}

/** Lyt på bestemte tabeller med et filter. status får kanalens tilstand ('SUBSCRIBED', 'CHANNEL_ERROR' …). Returnerer afmeld. */
export function abonnerTabeller(klient: SupabaseKlient, navn: string, tabeller: readonly string[], filter: string, fn: (tabel: string) => void, status?: (s: string) => void): () => void {
  let kanal = klient.channel(navn);
  for (const t of tabeller) kanal = kanal.on('postgres_changes', { event: '*', schema: 'public', table: t, filter }, (p) => fn(p.table));
  kanal.subscribe((s) => status?.(s));
  return () => { klient.removeChannel(kanal); };
}

/** En side lytter på hubben efter bestemte tabeller (fx I dag) – genbruger den fælles kanal, så der ikke åbnes flere.
 *  ok(true), mens forbindelsen er live – ellers bruger siden polling. Returnerer afmeld. */
export function lytTabeller(tabeller: readonly string[], fn: () => void, ok?: (live: boolean) => void): () => void {
  const s = new Set(tabeller);
  const af = live.lyt((t) => { if (t.has('*') || [...t].some((x) => s.has(x))) fn(); });
  const st = () => ok?.(live.status === 'live');
  st();
  const afS = live.lytStatus(st);
  return () => { af(); afS(); };
}
